/**
 * Voice AI — Guest Memory Tab
 * Memory CRUD grouped by category. Supports add/edit/delete.
 * Self-loading: fetches its own data when phone changes.
 * Dispatches: toast
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { guestStyles } from '../../styles/guest-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { guestService } from '../../services/guest-service.js';
import { confirmService } from '../../services/confirm-service.js';

const CATEGORIES = [
  { key: 'preference', label: 'Preferences', icon: 'fa-sliders' },
  { key: 'fact', label: 'Facts', icon: 'fa-circle-info' },
  { key: 'request', label: 'Requests', icon: 'fa-hand' },
  { key: 'complaint', label: 'Complaints', icon: 'fa-triangle-exclamation' },
  { key: 'note', label: 'Notes', icon: 'fa-note-sticky' },
];

export class GuestMemoryTab extends LitElement {
  static properties = {
    phone: { type: String },
    _memories: { state: true },
    _loading: { state: true },
    _showAddForm: { state: true },
    _editingId: { state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    guestStyles,
    css`
      .add-form {
        display: grid;
        grid-template-columns: 1fr 1fr 2fr;
        gap: var(--space-3);
        padding: var(--space-4);
        background: rgba(99, 102, 241, 0.04);
        border: 1px dashed rgba(99, 102, 241, 0.2);
        border-radius: var(--radius-lg);
        margin-bottom: var(--space-4);
        align-items: end;
      }

      .add-form .form-group {
        margin: 0;
      }
    `,
  ];

  constructor() {
    super();
    this.phone = null;
    this._memories = [];
    this._loading = true;
    this._showAddForm = false;
    this._editingId = null;
    this._lastPhone = null;
  }

  updated(changed) {
    if (changed.has('phone') && this.phone && this.phone !== this._lastPhone) {
      this._lastPhone = this.phone;
      this._loadMemories();
    }
  }

  async _loadMemories() {
    this._loading = true;
    try {
      const data = await guestService.listMemories(this.phone);
      if (data.success) {
        this._memories = data.memories || [];
      }
    } catch (err) {
      console.error('[Memory] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _toggleAddForm() {
    this._showAddForm = !this._showAddForm;
    this._editingId = null;
  }

  async _addMemory() {
    const form = this.shadowRoot;
    const category = form.getElementById('mem-category').value;
    const key = form.getElementById('mem-key').value.trim();
    const value = form.getElementById('mem-value').value.trim();

    if (!key || !value) {
      this._toast('Key and value are required', 'error');
      return;
    }

    try {
      const result = await guestService.addMemory({
        phone: this.phone,
        category,
        key,
        value,
      });
      if (result.success) {
        this._toast('Memory added');
        this._showAddForm = false;
        this._loadMemories();
      } else {
        this._toast(result.error || 'Add failed', 'error');
      }
    } catch (err) {
      this._toast('Add failed: ' + err.message, 'error');
    }
  }

  async _deleteMemory(memId) {
    const confirmed = await confirmService.show({
      title: 'Delete Memory',
      message: 'Delete this memory entry?',
      description: 'This cannot be undone.',
    });
    if (!confirmed) return;
    try {
      const result = await guestService.deleteMemory(this.phone, memId);
      if (result.success) {
        this._toast('Memory deleted');
        this._loadMemories();
      }
    } catch (err) {
      this._toast('Delete failed: ' + err.message, 'error');
    }
  }

  _startEdit(mem) {
    this._editingId = mem.id;
  }

  _cancelEdit() {
    this._editingId = null;
  }

  async _saveEdit(mem) {
    const row = this.shadowRoot.getElementById(`edit-row-${mem.id}`);
    if (!row) return;

    const value = row.querySelector('.edit-value').value.trim();
    if (!value) return;

    try {
      const result = await guestService.updateMemory(this.phone, mem.id, { value });
      if (result.success) {
        this._toast('Memory updated');
        this._editingId = null;
        this._loadMemories();
      }
    } catch (err) {
      this._toast('Update failed: ' + err.message, 'error');
    }
  }

  _toast(message, type = 'success') {
    this.dispatchEvent(new CustomEvent('toast', { detail: { message, type } }));
  }

  _groupByCategory() {
    const grouped = {};
    for (const cat of CATEGORIES) {
      grouped[cat.key] = [];
    }
    for (const mem of this._memories) {
      if (!grouped[mem.category]) grouped[mem.category] = [];
      grouped[mem.category].push(mem);
    }
    return grouped;
  }

  render() {
    if (this._loading) {
      return html`
        ${iconStyles}
        <div class="loading-spinner">
          <i class="fa-sharp fa-light fa-spinner-third"></i>
          Loading memories...
        </div>
      `;
    }

    const grouped = this._groupByCategory();
    const hasMemories = this._memories.length > 0;

    return html`
      ${iconStyles}
      <div class="glass-card">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--space-4);">
          <div>
            <div class="section-title">Guest Memory</div>
            <div class="section-desc">${this._memories.length} fact${this._memories.length !== 1 ? 's' : ''} stored</div>
          </div>
          <button class="btn btn-secondary" @click=${this._toggleAddForm}>
            <i class="fa-sharp fa-light ${this._showAddForm ? 'fa-xmark' : 'fa-plus'}"></i>
            ${this._showAddForm ? 'Cancel' : 'Add Memory'}
          </button>
        </div>

        ${this._showAddForm ? html`
          <div class="add-form">
            <div class="form-group">
              <label class="form-label">Category</label>
              <select id="mem-category" class="form-select">
                ${CATEGORIES.map(c => html`<option value=${c.key}>${c.label}</option>`)}
              </select>
            </div>
            <div class="form-group">
              <label class="form-label">Key</label>
              <input id="mem-key" class="form-input" type="text" placeholder="preferred_site" />
            </div>
            <div class="form-group">
              <label class="form-label">Value</label>
              <div style="display:flex;gap:var(--space-2);">
                <input id="mem-value" class="form-input" type="text" placeholder="Site 42, near the lake" style="flex:1;" />
                <button class="btn btn-primary" @click=${this._addMemory}>
                  <i class="fa-sharp fa-light fa-check"></i> Save
                </button>
              </div>
            </div>
          </div>
        ` : ''}

        ${!hasMemories ? html`
          <div style="text-align:center;padding:var(--space-8);color:var(--color-text-tertiary);font-size:var(--text-sm);">
            <i class="fa-sharp fa-light fa-brain" style="font-size:2rem;opacity:0.3;display:block;margin-bottom:var(--space-3);"></i>
            No memories yet. Memories are automatically extracted from conversations or can be added manually.
          </div>
        ` : html`
          ${CATEGORIES.filter(c => grouped[c.key]?.length > 0).map(cat => html`
            <div class="memory-category">
              <div class="memory-category-title">
                <i class="fa-sharp fa-light ${cat.icon}"></i>
                ${cat.label} (${grouped[cat.key].length})
              </div>
              ${grouped[cat.key].map(mem => this._editingId === mem.id
                ? this._renderEditRow(mem)
                : this._renderMemoryRow(mem)
              )}
            </div>
          `)}
        `}
      </div>
    `;
  }

  _renderMemoryRow(mem) {
    return html`
      <div class="memory-entry">
        <span class="memory-key">${mem.key}</span>
        <span class="memory-value">${mem.value}</span>
        <span class="memory-source">${mem.source}</span>
        <div class="memory-actions">
          <button class="memory-action-btn" @click=${() => this._startEdit(mem)} title="Edit">
            <i class="fa-sharp fa-light fa-pen"></i>
          </button>
          <button class="memory-action-btn delete" @click=${() => this._deleteMemory(mem.id)} title="Delete">
            <i class="fa-sharp fa-light fa-trash"></i>
          </button>
        </div>
      </div>
    `;
  }

  _renderEditRow(mem) {
    return html`
      <div class="memory-entry" id="edit-row-${mem.id}" style="background:rgba(99,102,241,0.04);border-radius:var(--radius-md);padding:var(--space-2) var(--space-3);">
        <span class="memory-key">${mem.key}</span>
        <input class="form-input edit-value" type="text" .value=${mem.value} style="flex:1;" />
        <div style="display:flex;gap:var(--space-1);">
          <button class="memory-action-btn" @click=${() => this._saveEdit(mem)} title="Save" style="color:#10b981;">
            <i class="fa-sharp fa-light fa-check"></i>
          </button>
          <button class="memory-action-btn" @click=${this._cancelEdit} title="Cancel">
            <i class="fa-sharp fa-light fa-xmark"></i>
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('guest-memory-tab', GuestMemoryTab);
